export enum PropertyTypeEnum {
  'select' = 'select',
  'number' = 'number',
  'section' = 'section',
  'datetime' = 'datetime',
  'text' = 'text',
  'action' = 'action',
  'action_group' = 'action_group',
}

export type PropertyType = PropertyTypeEnum

export interface PropertyBase {
  /**
   * @type string
   */
  id: string
  /**
   * @type string | undefined
   */
  label?: string
  /**
   * @type string | undefined
   */
  description?: string
  /**
   * @type string | undefined
   */
  tip?: string
  /**
   * @type boolean | undefined
   */
  required?: boolean
  /**
   * @type string | undefined
   */
  placeholder?: string
  /**
   * @type string | undefined
   */
  hide_condition?: string
}

export type PropertySelect = PropertyBase & {
  /**
   * @type string
   */
  type: PropertyType
  /**
   * @type array
   */
  options: {
    /**
     * @type string
     */
    value: string
    /**
     * @type string
     */
    label: string
    /**
     * @type string | undefined
     */
    description?: string
  }[]
  dynamic_options?: {
    enabled: boolean
    depends_on: string[]
  }
}

export type PropertyNumber = PropertyBase & {
  /**
   * @type string
   */
  type: PropertyType
  /**
   * @type number | undefined
   */
  min?: number
  /**
   * @type number | undefined
   */
  max?: number
  dynamic_options: any
}

export type PropertyWithId = PropertySelect | PropertyNumber

export interface PropertySection {
  /**
   * @type string
   */
  type: PropertyType
  /**
   * @type array
   */
  properties: PropertyWithId[]
  /**
   * @type boolean | undefined
   */
  inline?: boolean
  /**
   * @type string | undefined
   */
  hide_condition?: string


}

export type Property = PropertySelect | PropertyNumber | PropertySection

export enum NodeDefinitionCategoryEnum {
  'integration' = 'integration',
  'transformation' = 'transformation',
  'flow' = 'flow',
  'core' = 'core',
  'trigger' = 'trigger',
  'internal' = 'internal',
}

export interface NodeDefinition {
  /**
   * @pattern ^[^:]+:[^:]+$
   * @type string
   */
  type: string
  /**
   * @type string
   */
  color: string
  /**
   * @type string | undefined
   */
  text_color?: string
  /**
   * @type string
   */
  label: string
  /**
   * @type string
   */
  description: string
  /**
   * @type string | undefined
   */
  credentials?: any[]
  /**
   * @type string | undefined
   */
  image_src?: string
  /**
   * @type string
   */
  category: NodeDefinitionCategoryEnum
  /**
   * @type array | undefined
   */
  properties?: Property[]
  /**
   * @type object | undefined
   */
  default_properties_values?: {
    [key: string]: any
  }
  /**
   * @type array | undefined
   */
  actions?: any[]

  dynamic_properties?: {
    enabled: boolean
    depends_on: string[]
  }
}

export enum ModeEnum {
  'expression' = 'expression',
  'fixed' = 'fixed',
}

export enum ScenarioNodeOnErrorEnum {
  'stop' = 'stop',
  'continue' = 'continue',
  'fail_branch' = 'fail_branch',
}

export interface ScenarioNode {
  /**
   * @type string
   */
  id: string
  /**
   * @type string
   */
  type: string
  /**
   * @type string
   */
  label: string
  /**
   * @type string | undefined
   */
  invalid_tip?: string
  /**
   * @type object
   */
  properties: {
    [key: string]: any
  }
  /**
   * @type object
   */
  meta_properties: {
    [key: string]: {
      /**
       * @type string
       */
      visible?: boolean
      mode: ModeEnum
    }
  }
  /**
   * @default false
   * @type boolean | undefined
   */
  retry_on_fail?: boolean
  /**
   * @minLength 1
   * @maxLength 5
   * @default 3
   * @type number | undefined
   */
  max_retries?: number
  /**
   * @minLength 100
   * @maxLength 5000
   * @default 1000
   * @type number | undefined
   */
  retry_interval?: number
  /**
   * @default "stop"
   * @type string | undefined
   */
  on_error?: ScenarioNodeOnErrorEnum
  /**
   * @type object
   */
  position: {
    /**
     * @type number
     */
    x: number
    /**
     * @type number
     */
    y: number
  }
  /**
   * @type object
   */
  targets: {
    [key: string]: string
  }
}

export interface Scenario {
  /**
   * @minLength -2147483648
   * @maxLength 2147483647
   * @type integer
   */
  id: string
  /**
   * @minLength 0
   * @maxLength 2147483647
   * @type integer
   */
  workspace_id: string
  /**
   * @type string
   */
  created_at: string
  /**
   * @type string
   */
  updated_at: string
  /**
   * @type string
   */
  name: string
  /**
   * @type string
   */
  version_name: string | null
  /**
   * @type array
   */
  nodes: ScenarioNode[]
}

export interface ScenarioInsert {
  /**
   * @type string
   */
  name: string
  /**
   * @type string
   */
  version_name?: string | null
  /**
   * @type array
   */
  nodes: ScenarioNode[]
}

export interface ScenarioUpdate {
  /**
   * @type string | undefined
   */
  name?: string
  /**
   * @type string
   */
  version_name?: string | null
  /**
   * @type array
   */
  nodes: ScenarioNode[]
}

/**
 * @description Tasks API Index
 * @example [object Object]
 */
export interface GetV1200 {
  /**
   * @type string
   */
  message: string
}

export type GetV1QueryResponse = GetV1200

export type GetV1Query = {
  Response: GetV1200
  Errors: any
}

/**
 * @description The list of node definitions
 */
export type GetV1ScenariosNodesDefinitions200 = NodeDefinition[]

export type GetV1ScenariosNodesDefinitionsQueryResponse = GetV1ScenariosNodesDefinitions200

export type GetV1ScenariosNodesDefinitionsQuery = {
  Response: GetV1ScenariosNodesDefinitions200
  Errors: any
}

/**
 * @description The list of scenarios
 */
export interface GetV1Scenarios200 {
  /**
   * @minLength 0
   * @type number
   */
  count: number
  /**
   * @type array
   */
  data: Scenario[]
}

export type GetV1ScenariosQueryResponse = GetV1Scenarios200

export type GetV1ScenariosQuery = {
  Response: GetV1Scenarios200
  Errors: any
}

/**
 * @description The created scenario
 */
export type PostV1Scenarios200 = Scenario

/**
 * @description The validation error(s)
 */
export interface PostV1Scenarios422 {
  /**
   * @type boolean
   */
  success: boolean
  /**
   * @type object
   */
  error: {
    /**
     * @type array
     */
    issues: {
      /**
       * @type string
       */
      code: string
      /**
       * @type array
       */
      path: (string | number)[]
      /**
       * @type string | undefined
       */
      message?: string
    }[]
    /**
     * @type string
     */
    name: string
  }
}

/**
 * @description The scenario to create
 */
export type PostV1ScenariosMutationRequest = ScenarioInsert

export type PostV1ScenariosMutationResponse = PostV1Scenarios200

export type PostV1ScenariosMutation = {
  Response: PostV1Scenarios200
  Request: PostV1ScenariosMutationRequest
  Errors: PostV1Scenarios422
}

export interface GetV1ScenariosIdPathParams {
  /**
   * @type number
   */
  id: number | null
}

/**
 * @description The requested scenario
 */
export type GetV1ScenariosId200 = Scenario

/**
 * @description Scenario not found
 * @example [object Object]
 */
export interface GetV1ScenariosId404 {
  /**
   * @type string
   */
  message: string
}

/**
 * @description Invalid id error
 */
export interface GetV1ScenariosId422 {
  /**
   * @type boolean
   */
  success: boolean
  /**
   * @type object
   */
  error: {
    /**
     * @type array
     */
    issues: {
      /**
       * @type string
       */
      code: string
      /**
       * @type array
       */
      path: (string | number)[]
      /**
       * @type string | undefined
       */
      message?: string
    }[]
    /**
     * @type string
     */
    name: string
  }
}

export type GetV1ScenariosIdQueryResponse = GetV1ScenariosId200

export type GetV1ScenariosIdQuery = {
  Response: GetV1ScenariosId200
  PathParams: GetV1ScenariosIdPathParams
  Errors: GetV1ScenariosId404 | GetV1ScenariosId422
}

export interface PatchV1ScenariosIdPathParams {
  /**
   * @type number
   */
  id: number | null
}

/**
 * @description The updated scenario
 */
export type PatchV1ScenariosId200 = Scenario

/**
 * @description Scenario not found
 * @example [object Object]
 */
export interface PatchV1ScenariosId404 {
  /**
   * @type string
   */
  message: string
}

/**
 * @description The validation error(s)
 */
export type PatchV1ScenariosId422 =
  | {
    /**
     * @type boolean
     */
    success: boolean
    /**
     * @type object
     */
    error: {
      /**
       * @type array
       */
      issues: {
        /**
         * @type string
         */
        code: string
        /**
         * @type array
         */
        path: (string | number)[]
        /**
         * @type string | undefined
         */
        message?: string
      }[]
      /**
       * @type string
       */
      name: string
    }
  }
  | {
    /**
     * @type boolean
     */
    success: boolean
    /**
     * @type object
     */
    error: {
      /**
       * @type array
       */
      issues: {
        /**
         * @type string
         */
        code: string
        /**
         * @type array
         */
        path: (string | number)[]
        /**
         * @type string | undefined
         */
        message?: string
      }[]
      /**
       * @type string
       */
      name: string
    }
  }

/**
 * @description The scenario updates
 */
export type PatchV1ScenariosIdMutationRequest = ScenarioUpdate

export type PatchV1ScenariosIdMutationResponse = PatchV1ScenariosId200

export type PatchV1ScenariosIdMutation = {
  Response: PatchV1ScenariosId200
  Request: PatchV1ScenariosIdMutationRequest
  PathParams: PatchV1ScenariosIdPathParams
  Errors: PatchV1ScenariosId404 | PatchV1ScenariosId422
}

export interface DeleteV1ScenariosIdPathParams {
  /**
   * @type number
   */
  id: number | null
}

/**
 * @description Scenario deleted
 */
export type DeleteV1ScenariosId204 = any

/**
 * @description Scenario not found
 * @example [object Object]
 */
export interface DeleteV1ScenariosId404 {
  /**
   * @type string
   */
  message: string
}

/**
 * @description Invalid id error
 */
export interface DeleteV1ScenariosId422 {
  /**
   * @type boolean
   */
  success: boolean
  /**
   * @type object
   */
  error: {
    /**
     * @type array
     */
    issues: {
      /**
       * @type string
       */
      code: string
      /**
       * @type array
       */
      path: (string | number)[]
      /**
       * @type string | undefined
       */
      message?: string
    }[]
    /**
     * @type string
     */
    name: string
  }
}

export type DeleteV1ScenariosIdMutationResponse = DeleteV1ScenariosId204

export type DeleteV1ScenariosIdMutation = {
  Response: DeleteV1ScenariosId204
  PathParams: DeleteV1ScenariosIdPathParams
  Errors: DeleteV1ScenariosId404 | DeleteV1ScenariosId422
}